import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Sparkles, RefreshCw, ExternalLink, User, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Message, UserInfo } from "@/hooks/useAIConcierge";
import { useNavigate } from "react-router-dom";

interface AIConciergeProps {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  onSendMessage: (content: string) => void;
  onClear: () => void;
  userInfo: UserInfo | null;
  onUserInfoSubmit: (info: UserInfo) => void;
}

const QUICK_ACTIONS = [
  { label: "Join AI Community", message: "I want to join the HIBANE AI HUB community" },
  { label: "AI Training", message: "Tell me about your AI training programs" },
  { label: "Business Consulting", message: "I need AI consulting for my business" },
  { label: "Contact Team", message: "How can I contact the HIBANE team?" },
];

export const AIConciergePanel = ({
  messages,
  isLoading,
  error,
  onSendMessage,
  onClear,
  userInfo,
  onUserInfoSubmit,
}: AIConciergeProps) => {
  const [input, setInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when panel opens or user info is submitted
  useEffect(() => {
    if (userInfo) {
      inputRef.current?.focus();
    }
  }, [userInfo]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleUserInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");

    if (!nameInput.trim()) {
      return;
    }

    if (!validateEmail(emailInput)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    onUserInfoSubmit({
      name: nameInput.trim(),
      email: emailInput.trim().toLowerCase(),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      onSendMessage(input);
      setInput("");
    }
  };

  const handleQuickAction = (message: string) => {
    if (!isLoading) {
      onSendMessage(message);
    }
  };

  // Parse message content for links
  const renderMessageContent = (content: string) => {
    // Match internal links like /hub-registration, /trainwithhibane, etc.
    const linkRegex = /(\/[a-z-]+)/gi;
    const parts = content.split(linkRegex);

    return parts.map((part, index) => {
      if (part.match(linkRegex)) {
        return (
          <button
            key={index}
            onClick={() => navigate(part)}
            className="inline-flex items-center gap-1 text-primary underline hover:text-primary/80"
          >
            {part}
            <ExternalLink className="h-3 w-3" />
          </button>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className={cn(
        "fixed bottom-40 right-4 z-50 flex h-[500px] w-[360px] flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl md:bottom-24",
        "max-h-[calc(100vh-180px)] md:max-h-[600px]"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b bg-[#0f172a] px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          <div>
            <h3 className="font-semibold">HIBANE AI Concierge</h3>
            <p className="text-xs opacity-80">
              {userInfo ? `Hi, ${userInfo.name}!` : "Ask me anything about HIBANE"}
            </p>
          </div>
        </div>
        {userInfo && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClear}
            className="h-8 w-8 text-white hover:bg-white/10"
            title="Clear conversation"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* User Info Form - shown if no user info */}
      {!userInfo ? (
        <div className="flex flex-1 flex-col items-center justify-center p-6">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#0f172a]/10">
              <User className="h-8 w-8 text-[#0f172a]" />
            </div>
            <h4 className="mb-2 text-lg font-semibold">Welcome to HIBANE!</h4>
            <p className="text-sm text-muted-foreground">
              Before we chat, please introduce yourself so we can serve you better.
            </p>
          </div>

          <form onSubmit={handleUserInfoSubmit} className="w-full space-y-4">
            <div>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Your name"
                  className="pl-10"
                  required
                />
              </div>
            </div>
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setEmailError("");
                  }}
                  placeholder="Your email"
                  className={cn("pl-10", emailError && "border-destructive")}
                  required
                />
              </div>
              {emailError && (
                <p className="mt-1 text-xs text-destructive">{emailError}</p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={!nameInput.trim() || !emailInput.trim()}
            >
              Start Chat
            </Button>
          </form>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            Your info helps us personalize your experience and follow up if needed.
          </p>
        </div>
      ) : (
        <>
          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="flex flex-col gap-4">
                <div className="rounded-lg bg-muted p-4">
                  <p className="mb-2 text-sm text-foreground">
                    👋 Hi {userInfo.name}! I'm the HIBANE AI Concierge. I can help you with:
                  </p>
                  <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                    <li>Joining our free AI community (HUB)</li>
                    <li>Finding the right AI training program</li>
                    <li>AI consulting for your business</li>
                    <li>Connecting with our team</li>
                  </ul>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {QUICK_ACTIONS.map((action) => (
                    <Button
                      key={action.label}
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickAction(action.message)}
                      disabled={isLoading}
                      className="text-xs"
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <AnimatePresence mode="popLayout">
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={cn(
                        "flex",
                        message.role === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[85%] rounded-2xl px-4 py-2 text-sm",
                          message.role === "user"
                            ? "bg-[#0f172a] text-white"
                            : "bg-muted text-foreground"
                        )}
                      >
                        {message.role === "assistant" ? (
                          <div className="whitespace-pre-wrap">
                            {renderMessageContent(message.content)}
                            {isLoading && message === messages[messages.length - 1] && !message.content && (
                              <span className="inline-flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Thinking...
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
                
                {isLoading && messages.length > 0 && messages[messages.length - 1].role === "user" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="rounded-2xl bg-muted px-4 py-2">
                      <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Thinking...
                      </span>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
            
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive"
              >
                {error}
              </motion.div>
            )}
          </ScrollArea>

          {/* Input */}
          <form onSubmit={handleSubmit} className="border-t p-3">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                type="submit"
                size="icon"
                disabled={!input.trim() || isLoading}
                className="shrink-0"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </motion.div>
  );
};
